import React from 'react';
import { motion } from 'framer-motion';
import { AnimatedSection } from './ui/AnimatedSection';
import { Check, CheckCheck } from 'lucide-react';
type Message = {
  text: string;
  time: string;
  highlight?: boolean;
};
type Testimonial = {
  name: string;
  role: string;
  avatarColor: string;
  initials: string;
  messages: Message[];
};
const testimonials: Testimonial[] = [{
  name: 'Martín G.',
  role: 'Córdoba · 24 años',
  avatarColor: 'bg-[#6B8E23]',
  initials: 'MG',
  messages: [{
    text: 'Loco, agendé mi primera cita en la semana 2 🙌',
    time: '14:32'
  }, {
    text: 'Acabo de cobrar $180 USD por mi primer cierre. Pasé del Rappi a esto en menos de 2 meses.',
    time: '14:33',
    highlight: true
  }, {
    text: 'Gracias por el manual, en serio.',
    time: '14:34'
  }]
}, {
  name: 'Lucía F.',
  role: 'Buenos Aires · Estudiante',
  avatarColor: 'bg-[#8B5A3C]',
  initials: 'LF',
  messages: [{
    text: 'Empecé la semana pasada con 2hs por día entre clases',
    time: '09:12'
  }, {
    text: 'Hoy me confirmaron la primera comisión: 75 USD por una cita que se cerró 💸',
    time: '09:13',
    highlight: true
  }, {
    text: 'Mi viejo no lo puede creer jajaja',
    time: '09:14'
  }]
}, {
  name: 'Diego R.',
  role: 'Rosario · Empleado',
  avatarColor: 'bg-[#2D5F3F]',
  initials: 'DR',
  messages: [{
    text: 'Mes 1: 3 citas. Mes 2: 11 citas.',
    time: '20:45'
  }, {
    text: 'Este mes ya facturé más en dólares que mi sueldo en relación de dependencia.',
    time: '20:46',
    highlight: true
  }, {
    text: 'El guion del manual es ORO puro. No lo cambien nunca.',
    time: '20:47'
  }]
}];
const ChatBubble: React.FC<{
  message: Message;
}> = ({
  message
}) => {
  return <div className="flex justify-end" data-id="element-152">
      <div className={`relative max-w-[85%] rounded-lg px-3 py-2 ${message.highlight ? 'bg-[#DCF8C6] border border-[#a8e0a0]' : 'bg-[#DCF8C6]'} shadow-sm`} data-id="element-153">
        <p className="text-[13.5px] leading-snug text-[#1f2c33] font-sans" data-id="element-154">
          {message.text}
        </p>
        <div className="flex items-center justify-end gap-1 mt-1" data-id="element-155">
          <span className="text-[10px] text-[#667781]" data-id="element-156">{message.time}</span>
          <CheckCheck size={14} className="text-[#53bdeb]" data-id="element-157" />
        </div>
        {/* Bubble tail */}
        <div className={`absolute -right-1 bottom-0 w-3 h-3 ${message.highlight ? 'bg-[#DCF8C6]' : 'bg-[#DCF8C6]'}`} style={{
        clipPath: 'polygon(0 0, 100% 100%, 0 100%)'
      }} data-id="element-158" />
      </div>
    </div>;
};
const TestimonialCard: React.FC<{
  t: Testimonial;
  index: number;
}> = ({
  t,
  index
}) => {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.6,
    delay: index * 0.15
  }} className="bg-paper border border-ink/15 shadow-xl rounded-md overflow-hidden flex flex-col" data-id="element-159">
      {/* WhatsApp-style header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3" data-id="element-160">
        <div className={`w-10 h-10 rounded-full ${t.avatarColor} flex items-center justify-center text-white font-sans font-semibold text-sm flex-shrink-0`} data-id="element-161">
          {t.initials}
        </div>
        <div className="flex-grow min-w-0" data-id="element-162">
          <p className="font-sans font-semibold text-sm truncate" data-id="element-163">{t.name}</p>
          <p className="font-sans text-[11px] text-white/70 truncate" data-id="element-164">
            en línea
          </p>
        </div>
        <div className="text-white/60 text-xs font-sans hidden sm:block" data-id="element-165">
          {t.role}
        </div>
      </div>

      {/* Chat body */}
      <div className="flex-grow p-4 space-y-2" style={{
      backgroundColor: '#ECE5DD',
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill='%23d9d2c8' fill-opacity='0.4'%3E%3Cpath d='M0 0h20v20H0V0zm20 20h20v20H20V20z'/%3E%3C/g%3E%3C/svg%3E\")"
    }} data-id="element-166">
        {t.messages.map((m, i) => <ChatBubble key={i} message={m} data-id="element-167" />)}
      </div>

      {/* Caption / attribution */}
      <div className="bg-paper px-4 py-3 border-t border-ink/10 sm:hidden" data-id="element-168">
        <p className="font-sans text-xs text-ink/60" data-id="element-169">{t.role}</p>
      </div>
    </motion.div>;
};
export const Testimonials = () => {
  return <AnimatedSection className="bg-sepia/20 border-y border-ink/10" data-id="element-170">
      <div className="mb-14 text-center" data-id="element-171">
        <span className="uppercase tracking-widest text-xs font-semibold text-ink/50 block mb-4" data-id="element-172">
          TESTIMONIOS · CHATS REALES
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-ink leading-tight max-w-3xl mx-auto" data-id="element-173">
          Argentinos que ya cobran en{' '}
          <span className="italic text-accent" data-id="element-174">dólares</span>
        </h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-175">
        {testimonials.map((t, i) => <TestimonialCard key={t.name} t={t} index={i} data-id="element-176" />)}
      </div>

      <p className="text-center text-xs font-sans text-ink/50 mt-10 italic max-w-2xl mx-auto" data-id="element-177">
        Conversaciones compartidas por nuestros alumnos. Nombres reservados por
        privacidad.
      </p>
    </AnimatedSection>;
};